/* eslint-disable */
import React, { useState, useEffect } from 'react';
import * as Store from '@apply/store';
import { BusinessReferenceTypeOptions } from './constants/BusinessReferenceTypeOptions';
import { faEdit, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DataGrid } from 'devextreme-react';
import { ColumnFixing, Column, Paging, Pager } from 'devextreme-react/data-grid';
import { EditReferencesModal } from './modals/EditReferencesModal';
import { BusinessAndPeopleApi } from '@apply/api';
import { IReferencePanel } from '@apply/api/src/api/BusinessAndPeopleApi';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

const references: IReferencePanel[] = [];

interface IReferencesPanelParameters {
    applicationInsights: ApplicationInsights;
    currentTopLevelBusiness: number;
}

export const ReferencesPanel = ({ applicationInsights, currentTopLevelBusiness }: IReferencesPanelParameters): JSX.Element => {
    const businessAndPeopleApi = new BusinessAndPeopleApi.BusinessAndPeopleApi(applicationInsights);
    const [selectedBusinessId, set_selectedBusinessId] = useState<number>(0);

    const allowedPageSizes = [15, 25, 50];
    const defaultPageSize = allowedPageSizes[0];
    const [openModal, set_openModal] = useState<boolean>(false);
    const [selectedReference, set_selectedReference] = useState<IReferencePanel | undefined>();
    const [missingReferencesMessage, set_missingReferencesMessage] = useState<string | undefined>();

    useEffect(() => {
        if (selectedBusinessId != currentTopLevelBusiness) {
            set_selectedBusinessId(currentTopLevelBusiness);
        }
    }, [currentTopLevelBusiness]);

    const checkMissingReferences = () => {
        const missingReferences = [...BusinessReferenceTypeOptions];
        for (const referencePanelType of BusinessReferenceTypeOptions) {
            const foundReference = references.find((x) => x.type === referencePanelType.text);
            if (foundReference) {
                const index = missingReferences.findIndex((x) => x.text === foundReference.type && x.requiredAmount >= 0);
                if (index !== -1) {
                    missingReferences[index].requiredAmount--;
                }
            }
        }
        let message = '';
        message = missingReferences
            .slice(0, -1)
            .filter((x) => x.requiredAmount > 0)
            .map((x) => `${x.requiredAmount} ${x.text}`)
            .join(', ');
        const lastReference = missingReferences[missingReferences.length - 1];
        if (lastReference.requiredAmount > 0) {
            if (message === '') {
                message += `${lastReference.requiredAmount} ${lastReference.text}`;
            } else {
                message += ` and ${lastReference.requiredAmount} ${lastReference.text}`;
            }
        }

        set_missingReferencesMessage(message);
    };
    const onClickEditReference = (event, data) => {
        event.preventDefault();
        event.stopPropagation();
        set_selectedReference(data);
        set_openModal(true);
    };
    const onActionRender = (e: any) => {
        return (
            <div>
                <button
                    className='button gridActionColumnButton'
                    title='Edit Reference'
                    onClick={(event) => onClickEditReference(event, e.data)}
                >
                    <FontAwesomeIcon icon={faEdit} />
                </button>
            </div>
        );
    };

    const handleClose = () => {
        set_selectedReference(undefined);
        set_openModal(false);
    };

    const handleSave = (data: IReferencePanel, isUpdated: boolean) => {
        if (isUpdated) {
            const reference = references.findIndex((x) => x.id === data.id);
            references[reference] = data;
        } else {
            data.id = references.length + 1;
            references.push(data);
        }
        set_selectedReference(undefined);
        set_openModal(false);
    };

    useEffect(() => {
        checkMissingReferences();
    }, [openModal]);

    return (
        <>
            <div style={{ width: '100%', display: 'inline-block' }}>
                {missingReferencesMessage && (
                    <div
                        style={{
                            paddingBottom: '10px',
                            color: 'red',
                            width: '100%',
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{ paddingRight: '5px' }}
                        />
                        The following references are missing: {missingReferencesMessage}
                    </div>
                )}
                <div style={{ width: '100%', paddingBottom: '10px' }}>
                    <button
                        className='button gridActionColumnButton'
                        style={{
                            display: 'block',
                            marginLeft: 'auto',
                            marginRight: '0',
                        }}
                        onClick={(event) => {
                            onClickEditReference(event, undefined);
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faEdit}
                            style={{ paddingRight: '5px' }}
                        />
                        Add Reference
                    </button>
                </div>
                <div style={{ width: '100%' }}>
                    <DataGrid
                        id='referencesTable'
                        keyExpr='id'
                        dataSource={references}
                        showBorders={true}
                        showRowLines={true}
                        remoteOperations={false}
                        allowColumnResizing={true}
                        allowColumnReordering={false}
                        columnResizingMode={'nextColumn'}
                        columnMinWidth={20}
                        cellHintEnabled={true}
                        width={'auto'}
                        noDataText='No References found'
                    >
                        <ColumnFixing enabled={true} />
                        <Column
                            caption='Type'
                            dataField='type'
                            alignment={'left'}
                            minWidth={380}
                            allowSorting={false}
                        />
                        <Column
                            dataField='companyName'
                            caption='Company Name'
                            alignment='left'
                            minWidth={60}
                            allowSorting={false}
                        />
                        <Column
                            dataField='contactName'
                            caption='contactName'
                            alignment='left'
                            minWidth={60}
                            allowSorting={false}
                        />
                        <Column
                            caption=''
                            cellRender={onActionRender}
                            width={80}
                            alignment='center'
                        />
                        <Paging defaultPageSize={defaultPageSize} />
                        <Pager
                            showPageSizeSelector={allowedPageSizes.length > 1}
                            allowedPageSizes={allowedPageSizes}
                            showNavigationButtons={true}
                            showInfo={true}
                            visible={true}
                        />
                    </DataGrid>
                </div>
            </div>
            <EditReferencesModal
                applicationInsights={applicationInsights}
                selectedBusinessId={selectedBusinessId}
                referencePanel={selectedReference}
                isOpen={openModal}
                save={handleSave}
                close={handleClose}
            />
        </>
    );
};

export default ReferencesPanel;
